/**
 * api.js - UPDATED WITH UNIFIED MARKET-AWARE FUNCTIONS
 * =====================================================
 * 
 * CHANGES:
 * 1. Added unifiedAPI that automatically routes to India or US endpoints
 * 2. All existing APIs remain unchanged for backward compatibility
 * 
 * USAGE:
 * - Import unifiedAPI and pass selectedMarket from MarketContext
 * - unifiedAPI.searchStocks('TCS', 'IN') → calls /api/india/search
 * - unifiedAPI.searchStocks('AAPL', 'US') → calls /api/stocks/search
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// ===========================================
// UNIFIED MARKET-AWARE API (NEW)
// ===========================================
export const unifiedAPI = {
  /**
   * Search stocks - routes to appropriate market
   */
  searchStocks: async (query, market = 'US') => {
    const endpoint = market === 'IN' 
      ? `${API_URL}/india/search?q=${encodeURIComponent(query)}`
      : `${API_URL}/stocks/search?q=${encodeURIComponent(query)}`;
    
    console.log(`🔍 Searching ${market} market:`, endpoint);
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Search failed');
    return response.json();
  },

  /**
   * Get stock quote - routes to appropriate market
   */
  getQuote: async (symbol, market = 'US') => {
    const endpoint = market === 'IN'
      ? `${API_URL}/india/quote/${symbol}`
      : `${API_URL}/stocks/${symbol}`;
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch quote');
    return response.json();
  },

  /**
   * Get historical price data
   */
  getHistoricalData: async (symbol, timeframe = 'daily', market = 'US') => {
    const endpoint = market === 'IN'
      ? `${API_URL}/india/historical/${symbol}?timeframe=${timeframe}`
      : `${API_URL}/stocks/${symbol}/prices/${timeframe}`;
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch historical data');
    return response.json();
  },

  /**
   * Get company fundamentals/overview
   */
  getCompanyInfo: async (symbol, market = 'US') => {
    const endpoint = market === 'IN'
      ? `${API_URL}/india/fundamentals/${symbol}`
      : `${API_URL}/company/${symbol}/overview`;
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch company info');
    return response.json();
  },

  /**
   * Get technical indicators
   */
  getTechnicals: async (symbol, market = 'US') => {
    const endpoint = market === 'IN'
      ? `${API_URL}/india/technicals/${symbol}`
      : `${API_URL}/stocks/${symbol}/technicals`;
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch technicals');
    return response.json();
  },

  /**
   * Get options chain
   */
  getOptionsChain: async (symbol, expiration = null, market = 'US') => {
    let endpoint;
    if (market === 'IN') {
      endpoint = `${API_URL}/india/options/${symbol}`;
    } else {
      endpoint = expiration 
        ? `${API_URL}/options/${symbol}?expiration=${expiration}`
        : `${API_URL}/options/${symbol}`;
    }
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch options chain');
    return response.json();
  },

  /**
   * Get market indices
   */
  getIndices: async (market = 'US') => {
    const endpoint = market === 'IN'
      ? `${API_URL}/india/indices`
      : `${API_URL}/market/indices`;
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch indices');
    return response.json();
  },

  /**
   * Get news for a symbol
   */
  getNews: async (symbol, market = 'US') => {
    const endpoint = market === 'IN'
      ? `${API_URL}/india/news/${symbol}`
      : `${API_URL}/news/${symbol}?limit=10`;
    
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch news');
    return response.json();
  }
};

// ===========================================
// ORIGINAL US STOCK API (UNCHANGED)
// ===========================================
export const stockAPI = {
  getStockData: async (symbol) => {
    const response = await fetch(`${API_URL}/stocks/${symbol}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch stock data');
    }
    return response.json();
  },

  getCompanyInfo: async (symbol) => {
    const response = await fetch(`${API_URL}/stocks/${symbol}/company`);
    if (!response.ok) throw new Error('Failed to fetch company info');
    return response.json();
  },

  getAllStocks: async () => {
    const response = await fetch(`${API_URL}/stocks`);
    if (!response.ok) throw new Error('Failed to fetch stocks');
    return response.json();
  },

  getPriceData: async (symbol, timeframe) => {
    const response = await fetch(`${API_URL}/stocks/${symbol}/prices/${timeframe}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch price data');
    }
    return response.json();
  },

 getNews: async (symbol) => {
    const response = await fetch(`${API_URL}/news/${symbol}?limit=10`);
    if (!response.ok) throw new Error('Failed to fetch news');
    return response.json();
  },


getSectorData: async (symbol) => {
    const response = await fetch(`${API_URL}/sector/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch sector data');
    return response.json();
  },


getSocialSentiment: async (symbol) => {
    const response = await fetch(`${API_URL}/social/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch social sentiment');
    return response.json();
  },

  // Search stocks by symbol or name
  searchStocks: async (query) => {
    const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) throw new Error('Search failed');
    return response.json();
  }
};

// ===========================================
// INDIA MARKET API (UNCHANGED)
// ===========================================
export const indiaAPI = {
  /**
   * Get major NSE/BSE indices
   */
  getIndices: async () => {
    const response = await fetch(`${API_URL}/india/indices`);
    if (!response.ok) throw new Error('Failed to fetch India indices');
    return response.json();
  },

  /**
   * Get options chain for a symbol (NIFTY, BANKNIFTY, or stock)
   */
  getOptionsChain: async (symbol) => {
    const response = await fetch(`${API_URL}/india/options/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch India options');
    return response.json();
  },

  /**
   * Get Put-Call Ratio for a symbol
   */
  getPCR: async (symbol) => {
    const response = await fetch(`${API_URL}/india/pcr/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch PCR');
    return response.json();
  },

  /**
   * Get FII/DII activity data
   */
  getFIIDII: async () => {
    const response = await fetch(`${API_URL}/india/fii-dii`);
    if (!response.ok) throw new Error('Failed to fetch FII/DII data');
    return response.json();
  },

  /**
   * Get top gainers and losers
   */
  getMarketMovers: async () => {
    const response = await fetch(`${API_URL}/india/market-movers`);
    if (!response.ok) throw new Error('Failed to fetch market movers');
    return response.json();
  },

  /**
   * Get stock quote from India API (Alpha Vantage)
   */
  getStockQuote: async (symbol) => {
    const response = await fetch(`${API_URL}/india/quote/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch India stock quote');
    return response.json();
  },

  /**
   * Get historical data for India stock
   */
  getHistoricalData: async (symbol, timeframe = 'daily') => {
    const response = await fetch(`${API_URL}/india/historical/${symbol}?timeframe=${timeframe}`);
    if (!response.ok) throw new Error('Failed to fetch India historical data');
    return response.json();
  },

  /**
   * Get fundamentals for India stock
   */
  getFundamentals: async (symbol) => {
    const response = await fetch(`${API_URL}/india/fundamentals/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch India fundamentals');
    return response.json();
  },

  /**
   * Get technical indicators for India stock
   */
  getTechnicals: async (symbol) => {
    const response = await fetch(`${API_URL}/india/technicals/${symbol}`);
    if (!response.ok) throw new Error('Failed to fetch India technicals');
    return response.json();
  },

  /**
   * Search Indian stocks
   */
  searchStocks: async (query) => {
    const response = await fetch(`${API_URL}/india/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) throw new Error('India stock search failed');
    return response.json();
  },

  /**
   * Health check for India API
   */
  healthCheck: async () => {
    const response = await fetch(`${API_URL}/india/health`);
    if (!response.ok) throw new Error('India API health check failed');
    return response.json();
  }
};

// ===========================================
// MARKET API (UNCHANGED)
// ===========================================
export const marketAPI = {
  /**
   * Get current Federal Reserve indicators (LIVE DATA from FRED API)
   */
  getCurrentIndicators: async () => {
    try {
      const response = await fetch(`${API_URL}/market/indicators/current`);
      if (!response.ok) {
        throw new Error('Failed to fetch current indicators');
      }
      const data = await response.json();
      return data.indicators;
    } catch (error) {
      console.error('Failed to fetch current indicators:', error);
      // Return fallback mock data if API fails
      return {
        'Federal Funds Rate': {
          value: 5.33,
          change: 0,
          changePercent: 0,
          status: 'neutral',
          date: new Date().toISOString().split('T')[0]
        },
        'Inflation Rate (CPI)': {
          value: 315.0,
          change: 0.97,
          changePercent: 0.31,
          status: 'up',
          date: new Date().toISOString().split('T')[0]
        },
        'Unemployment Rate': {
          value: 4.1,
          change: -0.1,
          changePercent: -2.38,
          status: 'down',
          date: new Date().toISOString().split('T')[0]
        },
        'GDP Growth': {
          value: 2.8,
          change: -0.2,
          changePercent: -6.67,
          status: 'down',
          date: new Date().toISOString().split('T')[0]
        },
        '10-Year Treasury': {
          value: 4.35,
          change: 0.05,
          changePercent: 1.16,
          status: 'up',
          date: new Date().toISOString().split('T')[0]
        },
        'Consumer Confidence': {
          value: 61.3,
          change: -5.2,
          changePercent: -7.82,
          status: 'down',
          date: new Date().toISOString().split('T')[0]
        },
        'Retail Sales': {
          value: 710.5,
          change: 4.4,
          changePercent: 0.62,
          status: 'up',
          date: new Date().toISOString().split('T')[0]
        },
        'Housing Starts': {
          value: 1311,
          change: -113,
          changePercent: -7.93,
          status: 'down',
          date: new Date().toISOString().split('T')[0]
        }
      };
    }
  },

  /**
   * Get historical data for all indicators (LIVE DATA from FRED API)
   */
  getHistoricalData: async (years = 10) => {
    try {
      const response = await fetch(`${API_URL}/market/indicators/historical?years=${years}`);
      if (!response.ok) {
        throw new Error('Failed to fetch historical data');
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Failed to fetch historical data:', error);
      return {};
    }
  },

 /**
 * Get major market events
 */
getMarketEvents: async () => {
  try {
    const response = await fetch(`${API_URL}/market/events`);
    if (!response.ok) {
      throw new Error('Failed to fetch market events');
    }
    const data = await response.json();
    
    // Backend returns { events: [...] }, extract the array
    if (data.events && Array.isArray(data.events)) {
      return data.events;
    }
    
    // Fallback: if data itself is an array
    if (Array.isArray(data)) {
      return data;
    }
    
    console.error('Unexpected events response format:', data);
    return [];
  } catch (error) {
    console.error('Failed to fetch market events:', error);
    return [];
  }
},

  /**
   * Get major market indices (S&P, Dow, NASDAQ, etc.)
   */
  getMarketIndices: async () => {
    try {
      const response = await fetch(`${API_URL}/market/indices`);
      if (!response.ok) throw new Error('Failed to fetch indices');
      return response.json();
    } catch (error) {
      console.error('Failed to fetch market indices:', error);
      return [];
    }
  },

  /**
   * Get live ETF prices as index proxies
   */
  getLiveIndices: async () => {
    try {
      const response = await fetch(`${API_URL}/market/live-indices`);
      if (!response.ok) throw new Error('Failed to fetch live indices');
      return response.json();
    } catch (error) {
      console.error('Failed to fetch live indices:', error);
      return [];
    }
  },

  /**
   * Get FRED indicators (alias for getCurrentIndicators)
   */
  getFREDIndicators: async () => {
    return marketAPI.getCurrentIndicators();
  }
};

// ===========================================
// WATCHLIST API (UNCHANGED)
// ===========================================
export const watchlistAPI = {
  getWatchlist: async (userId) => {
    const response = await fetch(`${API_URL}/watchlist`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch watchlist');
    return response.json();
  },

  getWatchlistDetails: async (userId) => {
    const response = await fetch(`${API_URL}/watchlist/details`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch watchlist details');
    return response.json();
  },

  addToWatchlist: async (userId, symbol) => {
    const response = await fetch(`${API_URL}/watchlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify({ symbol })
    });
    if (!response.ok) throw new Error('Failed to add to watchlist');
    return response.json();
  },

  checkWatchlist: async (userId, symbol) => {
    try {
      const response = await fetch(`${API_URL}/watchlist`, {
        headers: { 'x-user-id': userId }
      });
      if (!response.ok) return { inWatchlist: false };
      const data = await response.json();
      const list = Array.isArray(data) ? data : (data.watchlist || []);
      const inWatchlist = list.some(item =>
        (item.symbol || item).toUpperCase() === symbol.toUpperCase()
      );
      return { inWatchlist };
    } catch {
      return { inWatchlist: false };
    }
  },

  removeFromWatchlist: async (userId, symbol) => {
    const response = await fetch(`${API_URL}/watchlist/${symbol}`, {
      method: 'DELETE',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to remove from watchlist');
    return response.json();
  },

  isInWatchlist: async (userId, symbol) => {
    const response = await fetch(`${API_URL}/watchlist/check/${symbol}`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) return false;
    const data = await response.json();
    return data.inWatchlist;
  }
};

// ===========================================
// ALERTS API (UNCHANGED)
// ===========================================
export const alertsAPI = {
  getAlerts: async (userId) => {
    const response = await fetch(`${API_URL}/alerts`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch alerts');
    return response.json();
  },

  createAlert: async (userId, alertData) => {
    const response = await fetch(`${API_URL}/alerts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify(alertData)
    });
    if (!response.ok) throw new Error('Failed to create alert');
    return response.json();
  },

  deleteAlert: async (userId, alertId) => {
    const response = await fetch(`${API_URL}/alerts/${alertId}`, {
      method: 'DELETE',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to delete alert');
    return response.json();
  },

  toggleAlert: async (userId, alertId) => {
    const response = await fetch(`${API_URL}/alerts/${alertId}/toggle`, {
      method: 'PATCH',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to toggle alert');
    return response.json();
  },

  sendTestEmail: async (userId) => {
    const response = await fetch(`${API_URL}/alerts/test-email`, {
      method: 'POST',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to send test email');
    return response.json();
  },

  sendDailySummary: async (userId) => {
    const response = await fetch(`${API_URL}/alerts/daily-summary`, {
      method: 'POST',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to send daily summary');
    return response.json();
  }
};

// ===========================================
// PORTFOLIO API (UNCHANGED)
// ===========================================
export const portfolioAPI = {
  getPortfolios: async (userId) => {
    const response = await fetch(`${API_URL}/portfolio`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch portfolios');
    return response.json();
  },

  createPortfolio: async (userId, name, description) => {
    const response = await fetch(`${API_URL}/portfolio/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify({ name, description })
    });
    if (!response.ok) throw new Error('Failed to create portfolio');
    return response.json();
  },

  getPortfolioDetails: async (userId, portfolioId) => {
    const response = await fetch(`${API_URL}/portfolio/${portfolioId}`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch portfolio details');
    return response.json();
  },

  addTransaction: async (userId, portfolioId, transaction) => {
    const response = await fetch(`${API_URL}/portfolio/${portfolioId}/transaction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify(transaction)
    });
    if (!response.ok) throw new Error('Failed to add transaction');
    return response.json();
  },

  deleteTransaction: async (userId, transactionId) => {
    const response = await fetch(`${API_URL}/portfolio/transaction/${transactionId}`, {
      method: 'DELETE',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to delete transaction');
    return response.json();
  },

  importCSV: async (userId, portfolioId, csvData, brokerFormat) => {
    const response = await fetch(`${API_URL}/portfolio/${portfolioId}/import-csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify({ csvData, brokerFormat })
    });
    if (!response.ok) throw new Error('Failed to import CSV');
    return response.json();
  },

  getPerformance: async (userId, portfolioId, days = 365) => {
    const response = await fetch(`${API_URL}/portfolio/${portfolioId}/performance?days=${days}`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch performance');
    return response.json();
  },

  deletePortfolio: async (userId, portfolioId) => {
    const response = await fetch(`${API_URL}/portfolio/${portfolioId}`, {
      method: 'DELETE',
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to delete portfolio');
    return response.json();
  }
};

// ===========================================
// PREFERENCES API (UNCHANGED)
// ===========================================
export const preferencesAPI = {
  getPreferences: async (userId) => {
    const response = await fetch(`${API_URL}/preferences`, {
      headers: {
        'x-user-id': userId
      }
    });
    if (!response.ok) throw new Error('Failed to fetch preferences');
    return response.json();
  },

  updatePreferences: async (userId, preferences) => {
    const response = await fetch(`${API_URL}/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify(preferences)
    });
    if (!response.ok) throw new Error('Failed to update preferences');
    return response.json();
  }
};

// ===========================================
// OPTIONS API (UNCHANGED)
// ===========================================
export const optionsAPI = {
  // Get full options chain
  getOptionsChain: async (symbol, expiration = null) => {
    const url = expiration 
      ? `${API_URL}/options/${symbol}?expiration=${expiration}`
      : `${API_URL}/options/${symbol}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch options chain');
    return response.json();
  },

  // Get available expiration dates
  getExpirations: async (symbol) => {
    const response = await fetch(`${API_URL}/options/${symbol}/expirations`);
    if (!response.ok) throw new Error('Failed to fetch expirations');
    return response.json();
  },

  // Get unusual activity
  getUnusualActivity: async (symbol) => {
    const response = await fetch(`${API_URL}/options/${symbol}/unusual`);
    if (!response.ok) throw new Error('Failed to fetch unusual activity');
    return response.json();
  },

  // Get Greeks
  getGreeks: async (symbol, expiration = null) => {
    const url = expiration
      ? `${API_URL}/options/${symbol}/greeks?expiration=${expiration}`
      : `${API_URL}/options/${symbol}/greeks`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch Greeks');
    return response.json();
  },

  // Filter options
  filterOptions: async (symbol, filters) => {
    const response = await fetch(`${API_URL}/options/${symbol}/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters)
    });
    if (!response.ok) throw new Error('Failed to filter options');
    return response.json();
  },

  // Get specific strike
  getStrike: async (symbol, strike, expiration = null) => {
    const url = expiration
      ? `${API_URL}/options/${symbol}/strike/${strike}?expiration=${expiration}`
      : `${API_URL}/options/${symbol}/strike/${strike}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch strike data');
    return response.json();
  }
};

// ===========================================
// RECESSION API (UNCHANGED)
// ===========================================
export const recessionAPI = {
  getProbability: async () => {
    const response = await fetch(`${API_URL}/recession/probability`);
    if (!response.ok) throw new Error('Failed to fetch recession probability');
    return response.json();
  },

  getIndicators: async () => {
    const response = await fetch(`${API_URL}/recession/indicators`);
    if (!response.ok) throw new Error('Failed to fetch recession indicators');
    return response.json();
  },

  getYieldCurve: async (years = 20) => {
    const response = await fetch(`${API_URL}/recession/yield-curve?years=${years}`);
    if (!response.ok) throw new Error('Failed to fetch yield curve data');
    return response.json();
  },

  getHistorical: async () => {
    const response = await fetch(`${API_URL}/recession/historical`);
    if (!response.ok) throw new Error('Failed to fetch historical recessions');
    return response.json();
  },

  getBusinessCycle: async () => {
    const response = await fetch(`${API_URL}/recession/business-cycle`);
    if (!response.ok) throw new Error('Failed to fetch business cycle');
    return response.json();
  },

  getSummary: async () => {
    const response = await fetch(`${API_URL}/recession/summary`);
    if (!response.ok) throw new Error('Failed to fetch recession summary');
    return response.json();
  },

  refresh: async () => {
    const response = await fetch(`${API_URL}/recession/refresh`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to refresh recession data');
    return response.json();
  }
};